﻿import React, {createContext, JSX, ReactNode, useCallback, useContext, useState} from "react";
import Dialog from "./Dialog";

interface DialogState<T = any> {
	content: ((close: (result: T) => void) => ReactNode) | null;
	isOpen: boolean;
	options: DialogOptions;
	resolve: ((value: T) => void) | null;
}

interface DialogContextType {
	showDialog: <T>(
		content: (close: (result: T) => void) => ReactNode,
		options?: DialogOptions
	) => Promise<T>;
}

const DialogContext = createContext<DialogContextType | null>(null);
const defaultDialogOptions: DialogOptions = {title: ""};

export function DialogProvider({children}: { children: ReactNode }): JSX.Element {
	const [dialogState, setDialogState] = useState<DialogState>({
		isOpen: false,
		resolve: null,
		content: null,
		options: defaultDialogOptions
	});

	const showDialog = useCallback(<T, >(
		content: (close: (result: T) => void) => ReactNode,
		options: DialogOptions = defaultDialogOptions
	): Promise<T> => {
		return new Promise<T>((resolve) => {
			setDialogState({
				isOpen: true,
				resolve,
				content,
				options
			});
		});
	}, []);

	const closeDialog = useCallback((result: any) => {
		if (dialogState.resolve) {
			dialogState.resolve(result);
		}
		setDialogState({
			isOpen: false,
			resolve: null,
			content: null,
			options: defaultDialogOptions
		});
	}, [dialogState.resolve]);

	return (
		<DialogContext.Provider value={{showDialog}}>
			{children}
			{dialogState.isOpen && <Dialog title={dialogState.options.title} onClose={closeDialog}>
				{dialogState.content?.(closeDialog)}
            </Dialog>}
		</DialogContext.Provider>
	);
}

export type DialogOptions = {
	title: string;
}

export function useDialog(): <T>(content: (close: (result: T) => void) => ReactNode, options?: DialogOptions) => Promise<T> {
	const context = useContext(DialogContext);
	if (!context) {
		throw new Error("useDialog must be used within a DialogProvider");
	}
	return context.showDialog;
}
